import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const validateEmail = (email) => {
  return email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
};

const SupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const BOT_NAME = process.env.REACT_APP_SUPPORT_BOT_NAME;
  const [messages, setMessages] = useState([
    { type: 'bot', text: `Hello, my name is ${BOT_NAME}. What is your name ?` }
  ]);
  const [currentInput, setCurrentInput] = useState('');
  const [currentStep, setCurrentStep] = useState('initial');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [error, setError] = useState('');
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const addBotMessageWithTypingEffect = async (message) => {
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 1000)); // Show typing for 1 second
    setIsTyping(false);
    setMessages(prev => [...prev, { type: 'bot', text: message }]);
  };

  const handleSubmitMessage = async (e) => {
    e.preventDefault();
    if (!currentInput.trim()) return;

    // Add user message
    const newMessage = { type: 'user', text: currentInput };
    setMessages(prev => [...prev, newMessage]);
    setCurrentInput('');

    try {
      if (currentStep === 'initial') {
        setFormData(prev => ({ ...prev, name: currentInput }));
        await addBotMessageWithTypingEffect(`Hi ${currentInput}! Please enter your email address.`);
        setCurrentStep('email');
      } else if (currentStep === 'email') {
        if (!validateEmail(currentInput)) {
          await addBotMessageWithTypingEffect('Please enter a valid email address.');
          return;
        }
        setFormData(prev => ({ ...prev, email: currentInput }));
        await addBotMessageWithTypingEffect('Great! What\'s the subject of your inquiry?');
        setCurrentStep('subject');
      } else if (currentStep === 'subject') {
        setFormData(prev => ({ ...prev, subject: currentInput }));
        await addBotMessageWithTypingEffect('Please describe your issue in detail.');
        setCurrentStep('message');
      } else if (currentStep === 'message') {
        const finalFormData = {
          ...formData,
          message: currentInput
        };

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_SUPPORT_CHAT_ENDPOINT}`, 
          finalFormData, 
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json'
            },
            timeout: parseInt(process.env.REACT_APP_API_TIMEOUT)
          }
        );

        if (response.data.message) {
          await addBotMessageWithTypingEffect('Thank you for your message! Our team will get back to you soon.');
          setIsInputDisabled(true);

          // Reset form and close chat after delay
          setTimeout(() => {
            setIsOpen(false);
            setMessages([{ type: 'bot', text: `Hello, my name is ${BOT_NAME}. How can I help?` }]);
            setCurrentStep('initial');
            setFormData({ name: '', email: '', subject: '', message: '' });
            setIsInputDisabled(false);
          }, parseInt(process.env.REACT_APP_CHAT_CLOSE_DELAY));
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message');
      await addBotMessageWithTypingEffect('Sorry, there was an error sending your message. Please try again.');
    }
  };

  return (
    <>
      {/* Support Icon Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-yellow-500 rounded-full shadow-lg flex items-center justify-center hover:bg-yellow-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 z-50"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-gray-900"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      </button>

      {/* Chat Widget */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 bg-gray-800 rounded-lg shadow-xl z-50">
          <div className="p-4 border-b border-gray-700 bg-gradient-to-r from-yellow-500 to-yellow-600">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-white p-1">
                  <img 
                    src={process.env.REACT_APP_BOT_AVATAR_URL}
                    alt={`${BOT_NAME} Avatar`}
                    className="w-full h-full rounded-full"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{BOT_NAME}</h3>
                  <p className="text-sm text-gray-800">Support Assistant</p>
                </div>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-800 hover:text-gray-900"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="h-96 overflow-y-auto p-4 space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} items-end space-x-2`}
              >
                {message.type === 'bot' && (
                  <div className="w-8 h-8 rounded-full bg-white p-1 flex-shrink-0">
                    <img 
                      src={process.env.REACT_APP_BOT_AVATAR_URL}
                      alt={`${BOT_NAME} Avatar`}
                      className="w-full h-full rounded-full"
                    />
                  </div>
                )}
                <div
                  className={`rounded-lg px-4 py-2 max-w-[80%] ${
                    message.type === 'user'
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-gray-700 text-gray-200'
                  }`}
                >
                  {message.text}
                </div>
                {message.type === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-gray-600 p-1 flex-shrink-0">
                    <img 
                      src={process.env.REACT_APP_USER_AVATAR_URL}
                      alt="User Avatar" 
                      className="w-full h-full rounded-full"
                    />
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start items-end space-x-2">
                <div className="w-8 h-8 rounded-full bg-white p-1 flex-shrink-0">
                  <img 
                    src={process.env.REACT_APP_BOT_AVATAR_URL}
                    alt={`${BOT_NAME} Avatar`}
                    className="w-full h-full rounded-full"
                  />
                </div>
                <div className="bg-gray-700 rounded-lg px-4 py-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmitMessage} className="p-4 border-t border-gray-700">
            {error && (
              <div className="mb-4 p-2 bg-red-900/50 border border-red-700 rounded text-red-200 text-sm">
                {error}
              </div>
            )}
            <div className="flex space-x-2">
              <input
                type="text"
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                disabled={isInputDisabled}
                className={`flex-1 bg-gray-700 border border-gray-600 rounded-md px-4 py-2 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent ${
                  isInputDisabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                placeholder={
                  currentStep === 'initial'
                    ? 'Enter your name...'
                    : currentStep === 'email'
                    ? 'Enter your email...'
                    : currentStep === 'subject'
                    ? 'Enter subject...'
                    : 'Enter your message...'
                }
              />
              <button
                type="submit"
                disabled={isInputDisabled}
                className={`bg-yellow-500 text-gray-900 px-4 py-2 rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 ${
                  isInputDisabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default SupportWidget;
